import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ArrowRight,
  Calculator,
  FileEdit,
  Info,
  Loader2,
  Mail,
  Minus,
  Plus,
  Settings,
  ClipboardList,
} from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { DatePicker } from '@/components/ui/DatePicker'
import { InfoPopover } from '@/components/ui/InfoPopover'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { useUserRole } from '@/hooks/useUserRole'
import {
  createDefaultPayment,
  getActiveDefaultEmails,
  getPropertyPaymentInfo,
  updateDefaultPayment,
  verifyEmailAddress,
} from '../services/reimbursementsService'
import { paymentFormSchema } from '../utils/reimbursementsValidators'
import type { PaymentFormValues } from '../types/schemas'
import type { DefaultEmail, PropertyPaymentInfo } from '../types'
import type { PaymentModalProps } from '../types'

const MODAL_FIELD =
  'mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-2xs outline-none transition focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/20'
const MODAL_FIELD_READONLY =
  'mt-1 h-10 w-full rounded-lg border border-slate-200/90 bg-slate-50/80 px-3 text-sm font-medium text-slate-800'
const MODAL_TEXTAREA =
  'mt-1 min-h-[72px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-2xs outline-none transition focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/20'
const MODAL_LABEL = 'text-xs font-semibold text-slate-600 uppercase tracking-wide'

const AMOUNT_HINT =
  'Input a positive number to reduce a previous payment. Click on the +/- symbol to toggle it.'

function formatMoney(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function parseAmount(raw: unknown): number {
  const num = Number(String(raw ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(num) ? num : 0
}

/** Angular `validateData` amount seed for payment form fields. */
function seedAmountString(raw: unknown, fallback = ''): string {
  if (raw === undefined || raw === null || String(raw).trim() === '') return fallback
  return parseAmount(raw).toFixed(2)
}

/**
 * Angular `dateChange`: ROLE_ADMIN + payment date before current month → amount editable on Configure.
 */
function isAdminPastMonthPaymentDate(paymentDate: string, isAdmin: boolean): boolean {
  if (!isAdmin || !paymentDate) return false
  const d = new Date(paymentDate.includes('T') ? paymentDate : `${paymentDate.slice(0, 10)}T12:00:00`)
  if (Number.isNaN(d.getTime())) return false
  const now = new Date()
  return (
    d.getFullYear() < now.getFullYear() ||
    (d.getFullYear() === now.getFullYear() && d.getMonth() < now.getMonth())
  )
}

function buildCityStateZip(info: PropertyPaymentInfo): string {
  if (!info.payment_city || !info.payment_zipcode) return ''
  return [info.payment_city, info.payment_state, info.payment_zipcode].filter(Boolean).join(', ')
}



function ReadonlyField({
  label,
  name,
  register,
}: {
  label: string
  name: keyof PaymentFormValues
  register: ReturnType<typeof useForm<PaymentFormValues>>['register']
}) {
  return (
    <label className="block min-w-0">
      <span className={MODAL_LABEL}>{label}</span>
      <input type="text" readOnly {...register(name)} className={MODAL_FIELD_READONLY} />
    </label>
  )
}

function VerifiedField({
  label,
  name,
  value,
  register,
}: {
  label: string
  name: keyof PaymentFormValues
  value: string
  register: ReturnType<typeof useForm<PaymentFormValues>>['register']
}) {
  const isVerified = String(value ?? '').trim().toUpperCase() === 'YES'
  return (
    <label className="block min-w-0">
      <span className={MODAL_LABEL}>{label}</span>
      <div className="mt-1 flex h-10 items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
        <span className="text-sm font-medium text-slate-700">{isVerified ? 'YES' : 'NO'}</span>
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
            isVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700',
          )}
        >
          {isVerified ? 'Verified' : 'Unverified'}
        </span>
      </div>
      <input type="hidden" {...register(name)} />
    </label>
  )
}

function AmountHintIcon() {
  return (
    <InfoPopover
      content={AMOUNT_HINT}
      maxWidth={320}
      align="start"
      testId="default-adjustment-hint"
      inline
    >
      <button
        type="button"
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 hover:text-slate-700"
        aria-label={AMOUNT_HINT}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
    </InfoPopover>
  )
}

function SignedAmountField({
  label,
  name,
  control,
  showHint,
  showSignToggle,
  isPositive,
  onToggleSign,
  disabled,
  error,
}: {
  label: string
  name: 'default_paid' | 'adjustment_amount'
  control: ReturnType<typeof useForm<PaymentFormValues>>['control']
  showHint?: boolean
  showSignToggle?: boolean
  isPositive: boolean
  onToggleSign: () => void
  disabled?: boolean
  error?: string
}) {
  return (
    <div className="block min-w-0">
      {label ? (
        <span className={`${MODAL_LABEL} inline-flex items-center`}>
          {label}
          {showHint ? <AmountHintIcon /> : null}
        </span>
      ) : null}
      <div className="relative">
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState }) => (
            <CurrencyInput
              value={field.value ?? ''}
              onChange={field.onChange}
              error={error ?? fieldState.error?.message}
              className={`${disabled ? MODAL_FIELD_READONLY : MODAL_FIELD} ${label ? '' : '!mt-0'} ${showSignToggle ? 'pl-8' : ''}`}
              disabled={disabled}
            />
          )}
        />
        {showSignToggle ? (
          <button
            type="button"
            onClick={onToggleSign}
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded p-0.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            aria-label={isPositive ? 'Toggle to negative amount' : 'Toggle to positive amount'}
            data-testid="payment-amount-sign-toggle"
          >
            {isPositive ? (
              <Plus className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Minus className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Angular `default-payment` Configure / Edit / Adjust Payment modal parity.
 */
function normalizeCheckNumber(raw?: string | null): string {
  const value = String(raw ?? '').trim()
  return value && value !== '0000' ? value : ''
}

export function PaymentModal({
  isOpen,
  onClose,
  onSuccess,
  defaultId,
  propertyId,
  mode = 'configure',
  selectedPaymentLogId,
  initialAmount,
  initialPaymentDate,
  selectedPaymentSeed,
  calculationData,
  adjustmentData,
  positiveAmount: positiveAmountProp,
  previousDefaultPayment = 0,
}: PaymentModalProps) {
  const { isAdmin } = useUserRole()
  const [propertyInfo, setPropertyInfo] = useState<PropertyPaymentInfo | null>(null)
  const [emails, setEmails] = useState<DefaultEmail[]>([])
  const [loading, setLoading] = useState(false)
  const [verifyingUserId, setVerifyingUserId] = useState<string | number | null>(null)
  const [positiveAmount, setPositiveAmount] = useState(Boolean(positiveAmountProp))
  /** Angular `amountEditable` — Admin + planned date before current month unlocks Configure amount. */
  const [amountEditable, setAmountEditable] = useState(false)

  const isEdit = Boolean(selectedPaymentLogId) || mode === 'edit'
  const isAdjustPayment = mode === 'adjust'
  const paymentAmountDisabled = !(isEdit || amountEditable)

  const seededCheckNumber = normalizeCheckNumber(selectedPaymentSeed?.check_number)
  const seededTransactionNotes = String(selectedPaymentSeed?.transaction_notes ?? '')
  const seededPaymentNotes = String(selectedPaymentSeed?.payment_notes ?? '')
  // Angular configure: seed loss_adjustment / partial / unused from amountData; edit forces loss to 0.00.
  const seededLossAdjustment = isEdit
    ? '0.00'
    : seedAmountString(calculationData?.loss_adjustment, '0.00')
  const seededPartialPayment = isEdit
    ? ''
    : seedAmountString(calculationData?.partial_payment_amount)
  const seededUnusedDeposit = isEdit
    ? ''
    : seedAmountString(calculationData?.unused_security_deposit)

  const form = useForm<PaymentFormValues, unknown, PaymentFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(paymentFormSchema) as never,
    defaultValues: {
      default_paid: initialAmount,
      adjustment_amount: initialAmount,
      resident_last_pay_amt: seededLossAdjustment,
      payment_source: '',
      payment_date: initialPaymentDate,
      partial_payment_amount: seededPartialPayment,
      unused_security_deposit: seededUnusedDeposit,
      transaction_notes: seededTransactionNotes,
      payment_notes: seededPaymentNotes,
      check_number: seededCheckNumber,
      charge_processing_fee: false,
      already_completed: false,
      payment_managed: '',
      property_name: '',
      pay_to: '',
      verify: '',
      payee_name: '',
      bank_name: '',
      routing_number: '',
      account_number: '',
      payment_address: '',
      city_state_zip: '',
    },
  })

  const isPaperCheck = propertyInfo?.payment_method === 'PAPER_CHECK'
  const isAch = propertyInfo?.payment_type === 'ACH'
  const chargeFee = Boolean(form.watch('charge_processing_fee'))
  const watchedPaid = form.watch('default_paid')
  const watchedAdjustment = form.watch('adjustment_amount')
  // Angular `adjustment_amt` / `paymentAmount` — 0 is falsy and hides "This Payment".
  const paymentAmount = parseAmount(
    isAdjustPayment
      ? (watchedAdjustment !== undefined && String(watchedAdjustment).trim() !== ''
          ? watchedAdjustment
          : initialAmount)
      : (watchedPaid !== undefined && String(watchedPaid).trim() !== ''
          ? watchedPaid
          : initialAmount),
  )
  const hasAdjustmentAmount = Boolean(paymentAmount)
  const signedAmount = (positiveAmount ? -1 : 1) * paymentAmount
  const feeAmount = chargeFee ? 15 : 0
  const thisPayment = signedAmount - feeAmount
  // Angular: when adjustment_amt is 0/falsy, Total to Pay = previousDefaultPayment only.
  const totalToPay = isAdjustPayment
    ? hasAdjustmentAmount
      ? thisPayment + previousDefaultPayment
      : previousDefaultPayment
    : thisPayment
  const companyId = propertyInfo?.fk_company_id

  useEffect(() => {
    if (!isOpen) return
    setPositiveAmount(Boolean(positiveAmountProp))
    setAmountEditable(isAdminPastMonthPaymentDate(initialPaymentDate, isAdmin))
  }, [isOpen, positiveAmountProp, initialPaymentDate, isAdmin])

  useEffect(() => {
    if (!isOpen || !propertyId) return
    let cancelled = false
    form.reset({
      default_paid: initialAmount,
      adjustment_amount: initialAmount,
      resident_last_pay_amt: seededLossAdjustment,
      payment_source: '',
      payment_date: initialPaymentDate,
      partial_payment_amount: seededPartialPayment,
      unused_security_deposit: seededUnusedDeposit,
      transaction_notes: seededTransactionNotes,
      payment_notes: seededPaymentNotes,
      check_number: seededCheckNumber,
      charge_processing_fee: false,
      already_completed: false,
      payment_managed: '',
      property_name: '',
      pay_to: '',
      verify: '',
      payee_name: '',
      bank_name: '',
      routing_number: '',
      account_number: '',
      payment_address: '',
      city_state_zip: '',
    })
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPropertyInfo(null)
    setEmails([])
    setLoading(true)
    Promise.all([getPropertyPaymentInfo(propertyId), getActiveDefaultEmails(propertyId)])
      .then(([info, em]) => {
        if (cancelled) return
        setPropertyInfo(info)
        setEmails(Array.isArray(em) ? em : [])
        form.setValue('property_name', String(info.property_name ?? ''))
        form.setValue('payment_managed', String(info.company_name ?? ''))
        form.setValue('pay_to', String(info.pay_to ?? ''))
        const verifyRaw = String(info.verify ?? '').trim().toUpperCase()
        form.setValue('verify', verifyRaw === '1' || verifyRaw === 'YES' ? 'YES' : 'NO')
        form.setValue('payment_source', String(info.payment_method ?? ''))
        form.setValue('payee_name', String(info.payee_user_name ?? ''))
        form.setValue('routing_number', String(info.routing_number ?? ''))
        form.setValue('account_number', String(info.account_number ?? ''))
        form.setValue('payment_address', String(info.payment_address ?? ''))
        form.setValue('city_state_zip', buildCityStateZip(info))
        form.setValue('charge_processing_fee', false)
        form.setValue('default_paid', initialAmount)
        form.setValue('adjustment_amount', initialAmount)
        form.setValue('payment_date', initialPaymentDate)
        form.setValue('resident_last_pay_amt', seededLossAdjustment)
        form.setValue('partial_payment_amount', seededPartialPayment)
        form.setValue('unused_security_deposit', seededUnusedDeposit)
        form.setValue('check_number', seededCheckNumber)
        form.setValue('transaction_notes', seededTransactionNotes)
        form.setValue('payment_notes', seededPaymentNotes)
      })
      .catch((e) => {
        if (!cancelled) showApiError(e)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    propertyId,
    initialAmount,
    initialPaymentDate,
    seededCheckNumber,
    seededTransactionNotes,
    seededPaymentNotes,
    seededLossAdjustment,
    seededPartialPayment,
    seededUnusedDeposit,
    form,
  ])

  const onSubmit = async (values: PaymentFormValues) => {
    const paidAmount = isAdjustPayment
      ? parseAmount(values.adjustment_amount)
      : parseAmount(values.default_paid)

    if (isAdjustPayment && String(values.adjustment_amount ?? '').trim() === '') {
      form.setError('adjustment_amount', { message: 'Amount is required' })
      return
    }
    if (!isAdjustPayment && String(values.default_paid ?? '').trim() === '') {
      form.setError('default_paid', { message: 'Amount is required' })
      return
    }

    try {
      const adjustedNotice = calculationData?.adjusted_notice
        ? String(calculationData.adjusted_notice)
        : undefined

      const payload = {
        default_paid: isAdjustPayment
          ? String(paidAmount)
          : values.default_paid,
        resident_last_pay_amt: values.resident_last_pay_amt,
        payment_source: values.payment_source,
        payment_date: values.payment_date,
        partial_payment_amount: values.partial_payment_amount,
        unused_security_deposit: values.unused_security_deposit,
        loss_adjustment: values.resident_last_pay_amt,
        transaction_notes: values.transaction_notes,
        payment_notes: values.payment_notes,
        check_number: values.check_number,
        charge_processing_fee: values.charge_processing_fee,
        already_completed: values.already_completed,
        property_name: values.property_name,
        payment_managed: values.payment_managed,
        pay_to: values.pay_to,
        verify: values.verify,
        payee_name: values.payee_name,
        bank_name: values.bank_name,
        routing_number: values.routing_number,
        account_number: values.account_number,
        payment_address: values.payment_address,
        city_state_zip: values.city_state_zip,
        ...(adjustedNotice ? { adjusted_notice: adjustedNotice } : {}),
        ...(calculationData ? { calculation_data: calculationData } : {}),
        ...(selectedPaymentLogId
          ? {}
          : adjustmentData
            ? { adjustment_data: adjustmentData }
            : {}),
        ...(positiveAmount ? { positive_amount: '1' as const } : {}),
        ...(isAdjustPayment ? { adjustment_amount: values.adjustment_amount } : {}),
      }
      if (selectedPaymentLogId) {
        await updateDefaultPayment(selectedPaymentLogId, payload)
      } else {
        await createDefaultPayment(defaultId, payload)
      }
      showSuccess('Payment Added Successfully!')
      onSuccess()
      onClose()
    } catch (e) {
      showApiError(e)
    }
  }

  const handleChangePaymentMethod = () => {
    if (!companyId) return
    onClose()
    window.open(`/company/edit/${companyId}#bankinginfo`, '_blank', 'noopener,noreferrer')
  }



  const handlePaymentDateChange = (value: string, onChange: (next: string) => void) => {
    onChange(value)
    setAmountEditable(isAdminPastMonthPaymentDate(value, isAdmin))
  }

  const handleVerifyEmail = async (row: DefaultEmail) => {
    if (!row.user_id || row.email_verify === 'Good') return
    setVerifyingUserId(row.user_id)
    try {
      const result = await verifyEmailAddress(row.user_id)
      // Angular: `if (response) { defaultEmail.email_verify = response }`
      if (!result) return
      setEmails((prev) =>
        prev.map((item) =>
          item.user_id === row.user_id ? { ...item, email_verify: result } : item,
        ),
      )
    } catch (e) {
      showApiError(e)
    } finally {
      setVerifyingUserId(null)
    }
  }

  const feeSummary = useMemo(
    () => ({
      previous: previousDefaultPayment
        ? formatMoney(previousDefaultPayment)
        : '',
      netDue: paymentAmount ? formatMoney(signedAmount) : '$0',
      // Angular: blank when adjustment_amt is 0/falsy — no tooltip on This Payment.
      thisPayment: hasAdjustmentAmount ? formatMoney(thisPayment) : '',
      fee: chargeFee ? '$15' : '$0',
      total: isAdjustPayment
        ? hasAdjustmentAmount
          ? formatMoney(thisPayment + previousDefaultPayment)
          : previousDefaultPayment
            ? formatMoney(previousDefaultPayment)
            : formatMoney(0)
        : paymentAmount
          ? formatMoney(totalToPay)
          : '$0',
    }),
    [
      previousDefaultPayment,
      paymentAmount,
      signedAmount,
      hasAdjustmentAmount,
      thisPayment,
      chargeFee,
      isAdjustPayment,
      totalToPay,
    ],
  )

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={isEdit ? 'Edit Payment' : 'Configure Payment'}
      subtitle="Review payment details and settlement preferences"
      onClose={onClose}
      maxWidth="max-w-1100"
      headerVariant="iconTitle"
      headerClassName="!bg-gradient-to-b !from-[#eef2f7] !to-[#dbe3ee] !border-b !border-[#d5dde8]"
      bodyScrollOnly
      panelDataTestId="configure-payment-modal-panel"
      footer={
        loading ? undefined : (
          <div
            className="flex items-center justify-end gap-4"
            data-testid="configure-payment-modal-footer"
          >
            <button
              type="button"
              disabled={form.formState.isSubmitting}
              onClick={onClose}
              data-testid="configure-payment-modal-cancel-button"
              className={secondaryEntityActionButtonClass}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={form.formState.isSubmitting}
              onClick={() => void form.handleSubmit(onSubmit)()}
              data-testid="configure-payment-modal-submit-button"
              className={cn(primaryEntityActionButtonClass, 'gap-1.5')}
            >
              {form.formState.isSubmitting ? 'Please wait…' : 'Submit Payment'}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )
      }
    >
      {loading ? (
        <div
          className="flex min-h-[320px] items-center justify-center"
          data-testid="configure-payment-modal-loading"
        >
          <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading payment info" />
        </div>
      ) : (
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 text-sm"
        data-testid="configure-payment-modal-form"
      >
        <div className="border-b border-slate-100 pb-5">
          <SectionHeader icon={<ClipboardList className="h-4 w-4" />} title="General Information" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <ReadonlyField label="Payment for" name="property_name" register={form.register} />
            <ReadonlyField label="Managed by" name="payment_managed" register={form.register} />
            <ReadonlyField label="Payment goes To" name="pay_to" register={form.register} />
          </div>
          <div className="mt-3 grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
            <VerifiedField
              label="Payment Info Verified"
              name="verify"
              value={String(form.watch('verify') ?? '')}
              register={form.register}
            />
            <div>
              <ReadonlyField label="Payment Method" name="payment_source" register={form.register} />
            </div>
            <div className="flex items-end pb-0.5">
              <button
                type="button"
                onClick={handleChangePaymentMethod}
                disabled={!companyId}
                className="h-10 w-full inline-flex items-center justify-center rounded-lg border border-[#1597ff] px-4 text-xs font-semibold text-[#1597ff] transition hover:bg-[#eaf4ff] disabled:opacity-50"
                data-testid="configure-payment-change-method-button"
              >
                Change Payment Method
              </button>
            </div>
          </div>
          {isPaperCheck ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <p className="m-0 text-xs font-medium text-amber-800">
                A $15 fee will be assessed for paper checks.
              </p>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-6 border-b border-slate-100 pb-5 lg:grid-cols-2">
          <div>
            <SectionHeader
              icon={<FileEdit className="h-4 w-4" />}
              title={isAch ? 'ACH Information' : 'Paper Check Information'}
              subtitle="Required only for paper checks"
            />
            {isAch ? (
              <div className="grid grid-cols-1 gap-3">
                <ReadonlyField label="ATTN" name="payee_name" register={form.register} />
                <ReadonlyField label="Routing" name="routing_number" register={form.register} />
                <ReadonlyField label="Accnt. Number" name="account_number" register={form.register} />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                <ReadonlyField label="ATTN" name="payee_name" register={form.register} />
                <ReadonlyField label="Address" name="payment_address" register={form.register} />
                <ReadonlyField
                  label="City, State, Zip"
                  name="city_state_zip"
                  register={form.register}
                />
              </div>
            )}
          </div>

          <div>
            <SectionHeader icon={<Mail className="h-4 w-4" />} title="Settlement Emails" />
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xs">
              <div className="max-h-[220px] overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">
                    <tr>
                      <th scope="col" className="px-3 py-2.5">Settlement Email</th>
                      <th scope="col" className="px-3 py-2.5 text-center w-20">Verify</th>
                      <th scope="col" className="px-3 py-2.5 text-left w-24">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {emails.length ? (
                      emails.map((row, index) => {
                        const statusText =
                          typeof row.email_verify === 'string' ? row.email_verify.trim() : ''
                        const verified = statusText === 'Good'
                        return (
                          <tr
                            key={`${row.email}-${row.user_id ?? index}`}
                            className="hover:bg-slate-50/70 transition-colors"
                          >
                            <td className="px-3 py-2 font-medium text-slate-800 break-all">
                              {row.email}
                            </td>
                            <td className="px-3 py-2 text-center align-middle">
                              {!verified ? (
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-blue-100 cursor-pointer"
                                  disabled={!row.user_id || verifyingUserId === row.user_id}
                                  onChange={() => void handleVerifyEmail(row)}
                                  aria-label={`Verify ${row.email}`}
                                />
                              ) : null}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              {verified ? (
                                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                                  Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                                  {statusText || 'Unverified'}
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-3 py-4 text-center text-xs text-slate-500">
                          No settlement emails.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="pt-1 text-[11px] font-medium text-slate-500">
              Note: Change the individual user&apos;s email address on the User Edit screen.
            </p>
          </div>
        </div>

        <div className="pb-2">
          <SectionHeader icon={<Settings className="h-4 w-4" />} title="Payment &amp; Options" />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {!isAdjustPayment ? (
              <SignedAmountField
                label="Payment"
                name="default_paid"
                control={form.control}
                showHint={isEdit || amountEditable}
                showSignToggle={isEdit}
                isPositive={positiveAmount}
                onToggleSign={() => setPositiveAmount((v) => !v)}
                disabled={paymentAmountDisabled}
                error={form.formState.errors.default_paid?.message}
              />
            ) : (
              <SignedAmountField
                label="Default Adjustment"
                name="adjustment_amount"
                control={form.control}
                showHint
                showSignToggle
                isPositive={positiveAmount}
                onToggleSign={() => setPositiveAmount((v) => !v)}
                error={form.formState.errors.adjustment_amount?.message}
              />
            )}
            <label className="block min-w-0">
              <span className={MODAL_LABEL}>Planned Payment Date</span>
              <Controller
                control={form.control}
                name="payment_date"
                render={({ field }) => (
                  <DatePicker
                    value={typeof field.value === 'string' ? field.value : ''}
                    onChange={(next) => handlePaymentDateChange(next, field.onChange)}
                    onBlur={field.onBlur}
                    allowFutureDates
                    showHelperText={false}
                    popperFixed
                    className={MODAL_FIELD}
                    error={form.formState.errors.payment_date?.message}
                  />
                )}
              />
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-6">
            {isPaperCheck ? (
              <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-blue-100"
                  {...form.register('charge_processing_fee')}
                />
                Charge Check Processing Fee ($15)?
              </label>
            ) : null}
            {isAdjustPayment ? (
              <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-blue-100"
                  {...form.register('already_completed')}
                />
                Already Completed?
              </label>
            ) : null}
            {isEdit ? (
              <label className="block min-w-0 flex-1 max-w-xs">
                <span className={MODAL_LABEL}>Check #</span>
                <input type="text" {...form.register('check_number')} className={MODAL_FIELD} />
              </label>
            ) : null}
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 pt-3 border-t border-slate-200/60">
            <label className="block min-w-0">
              <span className={MODAL_LABEL}>Payment Note</span>
              <textarea {...form.register('transaction_notes')} rows={2} className={MODAL_TEXTAREA} />
            </label>
            <label className="block min-w-0">
              <span className={MODAL_LABEL}>Check Note</span>
              <input type="text" {...form.register('payment_notes')} className={MODAL_FIELD} />
            </label>
          </div>
        </div>

        <div className="pt-2" data-testid="configure-payment-summary-section">
          <SectionHeader icon={<Calculator className="h-4 w-4" />} title="Payment Summary" />
          <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-1 text-xs">
              {isAdjustPayment ? (
                <>
                  <p className="m-0 flex items-center justify-between gap-6 text-slate-500">
                    <span>Previous Default Payment</span>
                    <span className="font-semibold text-slate-800">{feeSummary.previous || '$0'}</span>
                  </p>
                  <p className="m-0 flex items-center justify-between gap-6 text-slate-500">
                    <span>This Payment</span>
                    <span className="font-semibold text-slate-800">{feeSummary.thisPayment || '$0'}</span>
                  </p>
                </>
              ) : (
                <p className="m-0 flex items-center justify-between gap-6 text-slate-500">
                  <span>Default Net Due</span>
                  <span className="font-semibold text-slate-800">{feeSummary.netDue}</span>
                </p>
              )}
              <p className="m-0 flex items-center justify-between gap-6 text-slate-500">
                <span>Processing Fee</span>
                <span className="font-semibold text-slate-800">{feeSummary.fee}</span>
              </p>
            </div>
            <div className="rounded-xl bg-sky-50 px-4 py-2.5" data-testid="configure-payment-total-to-pay">
              <p className="m-0 text-xs font-semibold text-slate-600">Total to Pay</p>
              <p className="m-0 text-2xl font-bold text-slate-900">{feeSummary.total}</p>
              <p className="m-0 text-[11px] text-slate-500">Calculated from the payment details above</p>
            </div>
          </div>
        </div>

        <input type="hidden" {...form.register('resident_last_pay_amt')} />
        {!isAdjustPayment ? (
          <input type="hidden" {...form.register('adjustment_amount')} />
        ) : (
          <input type="hidden" {...form.register('default_paid')} />
        )}
      </form>
      )}
    </OnScreenModal>
  )
}
